import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Heading, Paragraph } from '@digdir/designsystemet-react';
import { UploadIcon } from '@navikt/aksel-icons';
import type { AssessedArea, DataSource } from '../../../api/workEnvironmentTypes';
import { useOpenProposalsMutation } from '../../../hooks/mutations/useWorkEnvironmentAssessmentMutations';
import { useSurveyActions } from '../../../hooks/useSurveyActions';
import { DataSourcesCard } from './DataSourcesCard';
import process from '../WorkEnvironmentSurveyProcess.module.css';

type Props = { surveyId: number; areas: AssessedArea[]; sources: DataSource[] };

/**
 * Step 4 is optional: the team leader first chooses whether to add data or move on. The safety
 * representative may add data too, but leaves moving on to the team leader.
 */
export const DataStepCard = ({ surveyId, areas, sources }: Props): ReactElement | null => {
  const { t } = useTranslation();
  const { may } = useSurveyActions();
  const [adding, setAdding] = useState(sources.length > 0);
  const open = useOpenProposalsMutation(surveyId);

  if (!may('advance')) {
    return may('addData') ? <DataSourcesCard surveyId={surveyId} areas={areas} sources={sources} /> : null;
  }
  if (adding) return <DataSourcesCard surveyId={surveyId} areas={areas} sources={sources} />;

  return (
    <section aria-labelledby="data-heading" className={process.card}>
      <div className={process.cardBody}>
        <div className={process.sideHeading}>
          <span className={`ds-label ${process.eyebrow}`} data-size="sm">
            {t('workEnvironmentRisk.data.next')}
          </span>
          <Heading level={2} data-size="sm" id="data-heading">
            {t('workEnvironmentRisk.data.heading')}
          </Heading>
          <Paragraph data-size="sm" className={process.subtle}>
            {t('workEnvironmentRisk.data.question')}
          </Paragraph>
        </div>
        {open.isError && (
          <Alert data-color="danger">
            <Paragraph>{t('workEnvironmentRisk.data.moveOnFailed')}</Paragraph>
          </Alert>
        )}
        <div className={process.linkRow}>
          {may('addData') && (
            <Button variant="primary" data-size="sm" onClick={() => setAdding(true)}>
              <UploadIcon aria-hidden />
              {t('workEnvironmentRisk.data.yes')}
            </Button>
          )}
          <Button variant="secondary" data-size="sm" loading={open.isPending} onClick={() => open.mutate(undefined)}>
            {t('workEnvironmentRisk.data.no')}
          </Button>
        </div>
      </div>
    </section>
  );
};
